import {
  FormTab,
  Group,
  GroupTitle,
  Toolbar,
  TransactionLogsField,
  TransactionNotesInput,
  TransactionNotesIsPrivateInput,
  ValidationSummary,
  useFieldLabel,
  useSaveMutation,
} from "ra-friendsofbabba";
import { NotifyResearcherButton, PrintButton } from "../component/button";
import React, { useCallback, useMemo } from "react";
import {
  TabbedForm,
  required,
  useNotify,
  usePermissions,
  useRedirect,
  useRefresh,
} from "react-admin";

import { ACCEPTED_DOCS_TYPES } from "../config";
import DocMediaInput from "./inputs/DocMediaInput";
import DynamicRadioInput from "./inputs/DynamicRadioInput";
import { Input } from "../component/input";
import OccupationDurationInput from "./inputs/OccupationDurationInput";
import PrintAlert from "../component/alert/PrintAlert";
import UserDocIDInput from "./inputs/UserDocIDInput";
import { UserInput } from "../component/input";
import UserProfileInput from "./inputs/UserProfileInput";
import UserResidenceInput from "./inputs/UserResidenceInput";
import { get } from "lodash";

const flagUserAsReady = () => {
  let profile = JSON.parse(localStorage.getItem("profile")) || {};
  localStorage.setItem(
    "profile",
    JSON.stringify({
      ...profile,
      permissions: (profile.permissions || []).concat("executive-projects"),
    })
  );
};
const refreshProfile = (newData) => {
  let profile = JSON.parse(localStorage.getItem("profile")) || {};
  localStorage.setItem(
    "profile",
    JSON.stringify({
      ...profile,
      ...newData?.user?.profile,
    })
  );
};
const Form = (props) => {
  const notify = useNotify();
  const refresh = useRefresh();
  const redirect = useRedirect();
  const fieldLabel = useFieldLabel({ ...props });
  const handleSuccess = useCallback(
    (response, values) => {
      const prevState = get(values, "transaction.state");
      const nextState = get(response, "data.state");
      const isMoved = prevState !== nextState;

      refreshProfile(values);

      notify(
        `resources.user-outputs.messages.${values.state}_${
          isMoved ? "moved" : "loop"
        }.save_success`
      );
      if (nextState === "waiting_approval") {
        flagUserAsReady();
        setTimeout(() => document.location.reload(), 3000);
      } else {
        if (values.id > 0) {
          refresh();
        } else {
          redirect("edit", props.basePath, response.data.id);
        }
      }
    },
    [notify, refresh, redirect, props.basePath]
  );
  const save = useSaveMutation({
    ...props,
    onSuccess: handleSuccess,
  });
  const { loaded, permissions } = usePermissions();
  const { isAdmin, isTro, isManager } = useMemo(
    () => ({
      isAdmin: loaded && permissions(["admin"]),
      isTro: loaded && permissions(["tro"]),
      isManager: loaded && permissions(["manager"]),
    }),
    [loaded, permissions]
  );

  return (
    <TabbedForm
      {...props}
      save={save}
      warnWhenUnsavedChanges
      toolbar={
        <Toolbar useWorkflow maxButtonsToDisplay={1}>
          {isAdmin || isManager ? (
            <NotifyResearcherButton source="user_id" visible={() => true} />
          ) : null}
        </Toolbar>
      }
    >
      <FormTab label={fieldLabel("user")}>
        {!isTro && <ValidationSummary />}
        <Input
          source="user_id"
          validate={required()}
          fullWidth
          component={<UserInput />}
        />
        <UserProfileInput
          fullWidth
          source="user.profile"
          label={fieldLabel("user.profile")}
        />
        <UserResidenceInput
          fullWidth
          source="user.profile.residence"
          label={fieldLabel("user.residence")}
        />
        <UserDocIDInput
          fullWidth
          source="user.profile.doc"
          label={fieldLabel("user.profile.doc")}
          help={fieldLabel("user.profile.doc.help")}
        />

        <Group wrapper fullWidth>
          <GroupTitle title={fieldLabel("logs")} />
          <TransactionLogsField source="logs" admin={isAdmin} />
        </Group>
        {(isAdmin || isManager) && (
          <TransactionNotesInput
            source="notes"
            helperText="resources.transactions.fields.notes.help"
            fullWidth
          />
        )}
        {(isAdmin || isManager) && (
          <TransactionNotesIsPrivateInput
            admin
            source="is_private"
            helperText="resources.transactions.fields.is_private.help"
            fullWidth
          />
        )}
      </FormTab>
      <FormTab label={fieldLabel("education")}>
        {!isTro && <ValidationSummary />}
        <Input
          sectionTitle="educational_qualifications"
          source="educational_qualification_id"
          optionText="title"
          table="educational-qualifications"
          fullWidth
          filter={{ graduated: true }}
          component={<DynamicRadioInput />}
        />
      </FormTab>
      <FormTab label={fieldLabel("occupation")}>
        {!isTro && <ValidationSummary />}
        <Input
          sectionTitle="occupations"
          source="occupation_id"
          optionText="occupation"
          table="occupations"
          fullWidth
          component={<DynamicRadioInput />}
        />
        <Input
          sectionTitle="occupation_durations"
          source="occupation_duration_id"
          optionText="duration"
          table="occupation-durations"
          fullWidth
          component={<OccupationDurationInput />}
        />
      </FormTab>
      <FormTab label={fieldLabel("signed_doc_media")}>
        {!isTro && <ValidationSummary fullWidth />}
        <PrintAlert>
          <PrintButton label="ra.action.generate_pos_d4" />
        </PrintAlert>
        <DocMediaInput
          source="signed_doc_media"
          title="filename"
          accept={ACCEPTED_DOCS_TYPES.join(",")}
        />
      </FormTab>
    </TabbedForm>
  );
};
export default Form;
